"use client";

import { useEffect, useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const MotionLink = motion(Link);
const tilePathMap: Record<string, string> = {
  "PASTELARIA & PANIFICADORAS": "/BakeryAndPastryPage",
  "BEBIDAS": "/DrinksPage",
  "CARVEJEIRA": "/CarvejeiraPage",
  "LACTICINIOS": "/DairyPage",
  "DESTILARIA": "/DistilleryPage"
};
export default function Casestudyshowcase() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const prefersReducedMotion = useReducedMotion();
  const tiles = [{
    id: "tile-a",
    src: "https://simple-elk.10web.cloud/wp-content/uploads/2026/07/Captura-de-ecra-2026-07-25-as-17.33.26-1.png?v=sfmmt7q00c9",
    srcset: "https://simple-elk.10web.cloud/wp-content/uploads/2026/07/Captura-de-ecra-2026-07-25-as-17.33.26-1.png?v=wuyzkju17eo-wvc-srcset",
    sizes: "https://simple-elk.10web.cloud/wp-content/uploads/2026/07/Captura-de-ecra-2026-07-25-as-17.33.26-1.png?v=elxo58pibdo-wvc-sizes",
    alt: "Renderização técnica de sistemas de engenharia industrial Eurocontrol",
    caption: "PASTELARIA & PANIFICADORAS\xA0",
    className: "md:col-span-4 md:row-span-2 aspect-[4/3] md:aspect-auto md:min-h-[560px]",
    isHero: true
  }, {
    id: "tile-b",
    src: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.17.png?v=tac76y6g6o",
    srcset: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.17.png?v=k83xkltzbic-wvc-srcset",
    sizes: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.17.png?v=arl4vh3jad-wvc-sizes",
    alt: "Interface de controlo de precisão para pulverização industrial",
    caption: "BEBIDAS",
    className: "md:col-span-2 aspect-[4/3]",
    isHero: false
  }, {
    id: "tile-c",
    src: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.23.png?v=icqfc7hu0uh",
    srcset: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.23.png?v=dd4k601q9xf-wvc-srcset",
    sizes: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.23.png?v=7oj19gim48y-wvc-sizes",
    alt: "Close-up de bicos pulverizadores de alta performance",
    caption: "CERVEJA",
    className: "md:col-span-2 aspect-[4/3]",
    isHero: false
  }, {
    id: "tile-d",
    src: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.28.png?v=h2gjf9puj7g",
    srcset: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.28.png?v=esuf4hce71-wvc-srcset",
    sizes: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.28.png?v=enyzpe3y41j-wvc-sizes",
    alt: "Visualização de aplicações industriais em ambiente fabril",
    caption: "LACTICINIOS",
    className: "md:col-span-3 aspect-[16/9]",
    isHero: false
  }, {
    id: "tile-e",
    src: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.37.png?v=vil9fot3sy",
    srcset: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.37.png?v=y2mclqwgzxo-wvc-srcset",
    sizes: "https://simple-elk.10web.cloud/wp-content/uploads/2026/08/Captura-de-ecra-2026-08-04-as-16.47.37.png?v=zlohp9koyj-wvc-sizes",
    alt: "Dashboard de eficiência e sustentabilidade em processos",
    caption: "DESTILARIA",
    className: "md:col-span-3 aspect-[16/9]",
    isHero: false
  }];
  useEffect(() => {
    if (!sectionRef.current) return;
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();
      mm.add({
        reduce: "(prefers-reduced-motion: reduce)",
        motion: "(prefers-reduced-motion: no-preference)"
      }, context => {
        const conditions = context.conditions as {
          reduce: boolean;
          motion: boolean;
        };
        if (conditions.reduce) {
          gsap.set("[data-reveal='intro'], [data-reveal='tile']", {
            opacity: 1,
            y: 0,
            clearProps: "all"
          });
          return;
        }
        gsap.from("[data-reveal='intro']", {
          opacity: 0,
          y: 24,
          duration: 0.7,
          ease: "cubic-bezier(0.22,0.61,0.36,1)",
          stagger: 0.08,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 82%"
          }
        });
        gsap.from("[data-reveal='tile']", {
          opacity: 0,
          y: 32,
          duration: 0.7,
          ease: "cubic-bezier(0.22,0.61,0.36,1)",
          stagger: 0.1,
          scrollTrigger: {
            trigger: "[data-grid='bento']",
            start: "top 85%"
          }
        });
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);
  return <section data-section-id="31406" ref={sectionRef} id="casestudyshowcase" data-nav="dark" className="bg-background text-foreground relative overflow-hidden cursor-pointer" style={{
    paddingBlock: "clamp(120px, 14vw, 200px)"
  }}>
      <div className="mx-auto w-full max-w-[1360px]" style={{
      paddingInline: "clamp(20px, 4vw, 56px)"
    }}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-end">
          <div className="lg:col-span-7">
            <div data-reveal="intro" className="text-primary uppercase font-medium" style={{
            letterSpacing: "0.22em",
            fontSize: "clamp(0.6875rem, 0.78vw, 0.8125rem)",
            lineHeight: 1
          }}>
              Soluções Eurocontrol
            </div>
            <h2 data-reveal="intro" className="font-default font-medium text-foreground mt-6" style={{
            fontSize: "clamp(2.25rem, 5vw, 4.5rem)",
            letterSpacing: "-0.03em",
            lineHeight: 1.02
          }}>Alimentação &amp; Bebidas</h2>
          </div>

          <div className="lg:col-span-5 lg:text-right">
            <p data-reveal="intro" className="text-foreground/80 !text-right" style={{
            fontSize: "clamp(1.0625rem, 1.35vw, 1.25rem)",
            letterSpacing: "-0.005em",
            lineHeight: 1.55
          }}>Dezenas de operações em unidades de processamento alimentar dependem da tecnologia de pulverização da Spraying Systems Co. De facto, a nossa tecnologia produz milhares de milhões de gotas por dia para aplicações que vão desde o revestimento e a secagem por pulverização até à limpeza, lavagem de tanques e lubrificação. Os nossos especialistas ajudam cada cliente a atingir objetivos de desempenho específicos, colocando a máxima prioridade na segurança alimentar.</p>
          </div>
        </div>

        <div data-grid="bento" className="mt-16 md:mt-24 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-6">
          {tiles.map((tile, i) => {
          const cleanCaption = tile.caption.trim();
          const targetPath = tilePathMap[cleanCaption];
          return <MotionLink key={tile.id} data-index={i} data-reveal="tile" to={targetPath || '#'} whileHover={prefersReducedMotion ? undefined : "hover"} whileFocus={prefersReducedMotion ? undefined : "hover"} initial="rest" animate="rest" tabIndex={0} className={`group relative overflow-hidden rounded-xl border border-border bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-4 focus-visible:ring-offset-background cursor-pointer ${tile.className}`} transition={{
            duration: 0.4,
            ease: [0.22, 0.61, 0.36, 1]
          }} variants={{
            rest: {},
            hover: {}
          }}>
                <motion.img src={tile.src} data-wvc-srcset={tile.srcset} data-wvc-sizes={tile.sizes} alt={tile.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" variants={{
              rest: {
                scale: 1
              },
              hover: {
                scale: 1.03
              }
            }} transition={{
              duration: 0.5,
              ease: [0.22, 0.61, 0.36, 1]
            }} />

                {tile.isHero && <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
              background: "linear-gradient(115deg, hsla(215, 80%, 20%, 0.22) 0%, hsla(215, 90%, 45%, 0.15) 30%, hsla(240, 8%, 4%, 0) 65%)"
            }} />}

                <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-3/5" style={{
              background: "linear-gradient(to top, hsla(240, 8%, 4%, 0.85) 0%, hsla(240, 8%, 4%, 0.35) 45%, hsla(240, 8%, 4%, 0) 100%)"
            }} />

                <motion.div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-xl border" variants={{
              rest: {
                opacity: 0
              },
              hover: {
                opacity: 1
              }
            }} transition={{
              duration: 0.4,
              ease: [0.22, 0.61, 0.36, 1]
            }} style={{
              borderColor: "hsla(215, 90%, 45%, 0.5)"
            }} />

                <motion.div variants={{
              rest: {
                y: 0
              },
              hover: {
                y: -4
              }
            }} transition={{
              duration: 0.4,
              ease: [0.22, 0.61, 0.36, 1]
            }} className="absolute bottom-5 left-5 z-10">
                  <div className="inline-flex items-center gap-2 rounded-lg border border-white/25 text-white font-semibold backdrop-blur-md" style={{
                padding: "10px 16px",
                backgroundColor: "hsla(240, 8%, 4%, 0.62)",
                fontSize: "clamp(0.9375rem, 1.15vw, 1.125rem)",
                letterSpacing: "0.04em",
                lineHeight: 1.35,
                textShadow: "0 1px 3px hsla(240, 8%, 4%, 0.65)"
              }}>
                    <span>{tile.caption}</span>
                  </div>
                </motion.div>
              </MotionLink>;
        })}
        </div>

        <div className="mt-16 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <p className="text-muted-foreground max-w-xl" style={{
          fontSize: "clamp(0.8125rem, 0.88vw, 0.9375rem)",
          letterSpacing: "0.02em",
          lineHeight: 1.45
        }}>
            Cada solução apresentada reflete o nosso compromisso com a inovação, 
            garantindo resultados superiores em todos os setores industriais.
          </p>
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90 h-13 rounded-lg gap-3 pl-7 pr-2" style={{
          height: "52px"
        }}>
            <span className="text-sm font-medium">Consulte-nos</span>
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-background text-primary transition-transform duration-300 group-hover:-rotate-45" aria-hidden="true">
              <ArrowUpRight className="h-4 w-4" />
            </span>
          </Button>
        </div>
      </div>
    </section>;
}